// [claude-code 2026-04-30] Canonical RiskFlow card anatomy shared across feed,
// Strategium, NarrativeFlow catalysts, and timeline surfaces.
import { useEffect, useState } from "react";
import type { FuseSeverity } from "../../lib/fuse-palette";
import { colorForSeverity } from "../../lib/fuse-palette";
import { NothingFuse } from "../shared/NothingFuse";
import { IVStack, type IVStackDirection } from "../shared/IVStack";
import { BrandIcon } from "../icon-bank/BrandIcon";
import { FintheonNumericText } from "../numbers/FintheonNumericText";
import {
  riskFlowDisplayDirection,
  type RiskFlowDirectionalForecastInput,
} from "../../lib/riskflow-feed";
import { useSettings } from "../../contexts/SettingsContext";

export interface RiskFlowCardAnatomyProps {
  title: string;
  sourceLabel?: string | null;
  timestampLabel?: string | null;
  expandedTimestampLabel?: string | null;
  severity: FuseSeverity;
  fuseScore: number;
  ivScore?: number | null;
  direction?: IVStackDirection;
  directionalInput?: RiskFlowDirectionalForecastInput;
  showDirectionalScoring?: boolean;
  instrument?: string | null;
  pointValue?: number | null;
  marketImpactText?: string | null;
  expanded?: boolean;
  compact?: boolean;
  selected?: boolean;
  fresh?: boolean;
  freshSurface?: boolean;
  showVerticalFuse?: boolean;
  showCollapsedMeta?: boolean;
  bodyClassName?: string;
  bodyMinHeightClass?: string;
  className?: string;
  children?: React.ReactNode;
}

export function RiskFlowCardAnatomy({
  title,
  sourceLabel,
  timestampLabel,
  expandedTimestampLabel,
  severity,
  fuseScore,
  ivScore,
  direction = "Neutral",
  directionalInput,
  showDirectionalScoring,
  instrument,
  pointValue,
  marketImpactText,
  expanded = false,
  compact = false,
  selected = false,
  fresh = false,
  freshSurface = fresh,
  showVerticalFuse = true,
  showCollapsedMeta = true,
  bodyClassName = "",
  bodyMinHeightClass,
  className = "",
  children,
}: RiskFlowCardAnatomyProps) {
  const { preferences } = useSettings();
  const [headlineRevealed, setHeadlineRevealed] = useState(!expanded);
  const severityColor = colorForSeverity(severity);
  const displayDirection = riskFlowDisplayDirection(direction, {
    ...directionalInput,
    ivScore,
    showDirectionalScoring:
      showDirectionalScoring ??
      preferences.riskflowFilters?.showDirectionalScoring ??
      true,
  });
  const verticalFuseValue = expanded
    ? 0
    : Math.min(1, Math.max(0.15, fuseScore / 10));
  const impactMeta = [
    instrument,
    formatPointValue(pointValue),
    marketImpactText,
  ].filter((item): item is string => Boolean(item));
  const shouldShowCollapsedMeta = showCollapsedMeta && !expanded;
  const showInlineMeta =
    shouldShowCollapsedMeta && (sourceLabel || timestampLabel);
  const showImpactMeta = shouldShowCollapsedMeta && impactMeta.length > 0;
  const showRightTimestamp = !showCollapsedMeta && timestampLabel;
  const showExpandedTimestamp = expanded && expandedTimestampLabel;
  const bodyPaddingClass = compact ? "px-2 py-2" : "px-3 py-2.5";
  const resolvedBodyMinHeightClass =
    bodyMinHeightClass || (compact ? "min-h-[58px]" : "min-h-[64px]");

  useEffect(() => {
    if (!expanded) {
      setHeadlineRevealed(false);
      return;
    }
    const raf = requestAnimationFrame(() => setHeadlineRevealed(true));
    return () => cancelAnimationFrame(raf);
  }, [expanded, title]);

  return (
    <div
      className={`group riskflow-card-anatomy relative overflow-hidden border-b bg-transparent transition-[opacity,transform,border-color,background-color] duration-200 hover:-translate-y-px ${
        expanded
          ? "riskflow-card-anatomy--expanded border-transparent"
          : "border-[var(--fintheon-accent)]/10 hover:border-[var(--fintheon-accent)]/22"
      } ${
        selected && !expanded ? "border-[var(--fintheon-accent)]/35" : ""
      } ${freshSurface ? "riskflow-flicker" : ""} ${className}`}
    >
      <div
        className={`flex items-stretch gap-3 ${bodyPaddingClass} ${resolvedBodyMinHeightClass} ${bodyClassName}`}
      >
        {showVerticalFuse && (
          <div className="riskflow-alert-fuse-rail flex w-[6px] shrink-0 items-stretch py-0.5">
            <NothingFuse
              value={verticalFuseValue}
              severity={severity}
              orientation="vertical"
              thickness={6}
              animateIn={fresh}
              className="riskflow-alert-fuse"
            />
          </div>
        )}

        <div className="min-w-0 flex-1 self-center">
          <p
            className={`t-text-reveal font-medium leading-snug text-[var(--fintheon-text)] ${
              compact ? "text-[11px]" : "text-xs"
            } ${
              expanded
                ? "line-clamp-none max-[767px]:pr-[7.5rem]"
                : "line-clamp-3"
            }`}
            data-open={
              expanded ? (headlineRevealed ? "true" : "false") : "true"
            }
            style={{ textWrap: expanded ? "pretty" : "balance" }}
          >
            {title}
          </p>
          {showInlineMeta && (
            <div className="mt-1 flex items-center gap-1.5 text-[9px] uppercase tracking-[0.08em] text-[var(--fintheon-text)]/35">
              {sourceLabel && (
                <span className="inline-flex min-w-0 items-center gap-1 truncate">
                  <BrandIcon
                    name={sourceLabel}
                    className="h-3 w-3 text-[var(--fintheon-accent)]/55"
                    fallback
                  />
                  <span className="truncate">{sourceLabel}</span>
                </span>
              )}
              {sourceLabel && timestampLabel && (
                <span className="text-[var(--fintheon-text)]/20">&middot;</span>
              )}
              {timestampLabel && (
                <span className="shrink-0 normal-case lowercase tabular-nums">
                  {timestampLabel}
                </span>
              )}
            </div>
          )}
          {showImpactMeta && (
            <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[9px] uppercase tracking-[0.08em] text-[var(--fintheon-accent)]/55">
              {impactMeta.map((item, index) => (
                <span key={`${item}-${index}`} className="min-w-0 truncate">
                  {index > 0 && (
                    <span className="mr-1 text-[var(--fintheon-text)]/20">
                      |
                    </span>
                  )}
                  <FintheonNumericText
                    forceStatic={index !== 1}
                    kind={index === 1 ? "point-change" : undefined}
                    text={item}
                  />
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2.5">
          {showExpandedTimestamp && (
            <span
              className="riskflow-expanded-timestamp t-text-reveal whitespace-nowrap font-mono text-[11px] leading-none text-[var(--fintheon-text)]/48 tabular-nums max-[767px]:absolute max-[767px]:top-3 max-[767px]:right-3 sm:text-xs"
              data-open={headlineRevealed ? "true" : "false"}
            >
              {expandedTimestampLabel}
            </span>
          )}
          <div
            className={`flex shrink-0 flex-col items-end ${
              showRightTimestamp && !expanded
                ? "justify-start gap-1.5 pt-0.5"
                : "justify-center"
            }`}
          >
            {showRightTimestamp && (
              <span
                className={`riskflow-corner-timestamp overflow-hidden font-mono text-[9px] leading-none lowercase tracking-[0.04em] text-[var(--fintheon-text)]/34 tabular-nums transition-[max-height,opacity,transform] duration-200 ease-out ${
                  expanded
                    ? "max-h-0 -translate-y-1 opacity-0"
                    : "max-h-3 translate-y-0 opacity-100"
                }`}
                aria-hidden={expanded}
              >
                {timestampLabel}
              </span>
            )}
            <IVStack
              score={ivScore}
              direction={displayDirection}
              color={severityColor}
              width={compact ? 32 : 36}
              fontSize={compact ? 11 : 13}
              chevronSize={compact ? 12 : 14}
            />
          </div>
        </div>
      </div>

      {children}
    </div>
  );
}

function formatPointValue(value: number | null | undefined): string | null {
  if (value == null || !Number.isFinite(Number(value))) return null;
  const numeric = Number(value);
  const magnitude = Math.abs(numeric);
  const formatted =
    magnitude >= 10
      ? numeric.toFixed(0)
      : magnitude >= 1
        ? numeric.toFixed(1)
        : magnitude >= 0.01
          ? numeric.toFixed(4)
          : numeric.toFixed(6);
  return `IV +/-${formatted} pts`;
}
